import React, { useState, useEffect } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';
import {
    Button,
    CircularLoader,
    NoticeBox,
    InputField,
    IconEdit16,
    IconDelete16,
    IconView16
} from '@dhis2/ui';
import { STAFF_TEI_TYPE, STAFF_ATTRIBUTES, POSITION_ATTRIBUTES } from '../api/constants';

// ✅ Define protected users that cannot be deleted
const PROTECTED_USERS = [
    'admin',
    'administrator',
    'superadmin',
    'ceo',
    'chief',
    'director',
];

// ✅ Define protected role IDs
const PROTECTED_ROLE_IDS = [
    'nLSSLch316I', // CEO role ID
];

// ✅ Helper to format duration
const formatDuration = (lastLoginDate) => {
    if (!lastLoginDate) return 'Never logged in';
    
    const now = new Date();
    const last = new Date(lastLoginDate);
    const diffMs = now - last;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffWeeks < 4) return `${diffWeeks}w ago`;
    if (diffMonths < 12) return `${diffMonths}mo ago`;
    return `${Math.floor(diffDays / 365)}y ago`;
};

// ✅ Helper to get status color based on last login
const getActivityStatus = (lastLoginDate) => {
    if (!lastLoginDate) return { label: 'Inactive', color: '#dc3545', bg: '#f8d7da' };
    
    const now = new Date();
    const last = new Date(lastLoginDate);
    const diffHours = (now - last) / 3600000;
    
    if (diffHours < 1) return { label: 'Online', color: '#28a745', bg: '#d4edda' };
    if (diffHours < 24) return { label: 'Today', color: '#17a2b8', bg: '#d1ecf1' };
    if (diffHours < 168) { // 7 days
        const days = Math.floor(diffHours / 24);
        return { label: `${days}d ago`, color: '#ffc107', bg: '#fff3cd' };
    }
    return { label: 'Inactive', color: '#dc3545', bg: '#f8d7da' };
};

// ✅ Helper to format last login date
const formatLastLogin = (lastLoginDate) => {
    if (!lastLoginDate) return '—';
    const date = new Date(lastLoginDate);
    return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const StaffList = ({ onEdit, onDelete, onView }) => {
    const engine = useDataEngine();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [staffMembers, setStaffMembers] = useState([]);
    const [currentOrgUnit, setCurrentOrgUnit] = useState(null);
    const [search, setSearch] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [deleteLoading, setDeleteLoading] = useState(null);
    const [userActivityMap, setUserActivityMap] = useState({});
    const [loadingActivity, setLoadingActivity] = useState(true);

    // ✅ Function to check if a user is protected
    const isProtectedUser = (staff) => {
        const username = staff.userCredentials?.username?.toLowerCase() || staff.username?.toLowerCase() || '';
        const userRoles = staff.userCredentials?.userRoles || [];
        const roleIds = userRoles.map(role => role.id);

        if (PROTECTED_USERS.includes(username)) return true;
        if (roleIds.some(roleId => PROTECTED_ROLE_IDS.includes(roleId))) return true;
        const roleNames = userRoles.map(role => role.name?.toLowerCase() || '');
        if (roleNames.some(name => name.includes('ceo') || name.includes('chief executive'))) return true;
        return false;
    };

    // ✅ Fetch user activity data
    const fetchUserActivity = async (userIds) => {
        if (!userIds || userIds.length === 0) return {};
        
        const activityMap = {};
        const batchSize = 50;
        
        for (let i = 0; i < userIds.length; i += batchSize) {
            const batch = userIds.slice(i, i + batchSize);
            
            try {
                // Try to get last login from user API
                const filter = batch.map(id => `id:eq:${id}`).join(';');
                const response = await engine.query({
                    users: {
                        resource: 'users',
                        params: {
                            filter: filter,
                            fields: 'id,lastLogin,userCredentials[lastLogin]',
                            pageSize: batchSize
                        }
                    }
                });
                
                const users = response.users?.users || [];
                users.forEach(user => {
                    // DHIS2 stores lastLogin in userCredentials or directly on user
                    const lastLogin = user.userCredentials?.lastLogin || user.lastLogin || null;
                    activityMap[user.id] = {
                        lastLogin: lastLogin,
                        hasLoggedIn: !!lastLogin
                    };
                });
                
            } catch (error) {
                console.warn('⚠️ Failed to fetch activity for batch:', error);
                // Fallback: mark users as unknown
                batch.forEach(id => {
                    activityMap[id] = {
                        lastLogin: null,
                        hasLoggedIn: false,
                        error: true
                    };
                });
            }
        }
        
        return activityMap;
    };

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            try {
                setLoading(true);
                setLoadingActivity(true);
                setError(null);

                // 1. Get current user's org unit
                const { me } = await engine.query({
                    me: {
                        resource: 'me',
                        params: {
                            fields: 'organisationUnits[id,name,level],dataViewOrganisationUnits[id,name,level]'
                        }
                    }
                });

                if (!me.organisationUnits?.length && !me.dataViewOrganisationUnits?.length) {
                    throw new Error('No organization units assigned to current user');
                }

                const allUserOrgUnits = [
                    ...(me.organisationUnits || []),
                    ...(me.dataViewOrganisationUnits || [])
                ];

                const primaryOrgUnit = allUserOrgUnits[0];
                setCurrentOrgUnit(primaryOrgUnit);

                // 2. Fetch ALL users
                const usersResponse = await engine.query({
                    users: {
                        resource: 'users',
                        params: {
                            fields: 'id,firstName,surname,email,phoneNumber,lastLogin,userCredentials[username,lastLogin,userRoles[id,name]],organisationUnits[id,name,level],attributeValues[attribute,value]',
                            pageSize: 1000,
                            filter: 'userCredentials.disabled:eq:false'
                        }
                    }
                });

                const allUsers = usersResponse.users?.users || [];

                // 3. Fetch TEIs with ACCESSIBLE mode
                const teisResponse = await engine.query({
                    teis: {
                        resource: 'trackedEntityInstances',
                        params: {
                            trackedEntityType: STAFF_TEI_TYPE,
                            fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
                            ou: primaryOrgUnit.id,
                            ouMode: 'ACCESSIBLE',
                            pageSize: 1000,
                            filter: `${STAFF_ATTRIBUTES.USERNAME}:!EQ:null`
                        }
                    }
                });

                if (!isMounted) return;

                let allTeis = teisResponse.teis?.trackedEntityInstances || [];

                // 4. Get org unit hierarchy for filtering
                const hierarchyOrgUnits = new Set();
                allTeis.forEach(tei => {
                    if (tei.orgUnit) {
                        hierarchyOrgUnits.add(tei.orgUnit);
                    }
                });

                for (const userOu of allUserOrgUnits) {
                    try {
                        const hierarchyResponse = await engine.query({
                            organisationUnits: {
                                resource: 'organisationUnits',
                                params: {
                                    fields: 'id',
                                    filter: `path:like:${userOu.id}`,
                                    paging: false
                                }
                            }
                        });
                        hierarchyResponse.organisationUnits?.forEach(ou => {
                            hierarchyOrgUnits.add(ou.id);
                        });
                    } catch (e) {
                        // ignore
                    }
                }

                // 5. Match users with TEIs
                const inScopeUsers = [];
                allUsers.forEach(user => {
                    const userUsername = user.userCredentials?.username;
                    const userTeis = userUsername ? allTeis.filter(tei => {
                        const usernameAttr = tei.attributes?.find(attr =>
                            attr.attribute === STAFF_ATTRIBUTES.USERNAME
                        );
                        return usernameAttr?.value === userUsername;
                    }) : [];

                    const userOrgUnitIds = [
                        ...(user.organisationUnits?.map(ou => ou.id) || []),
                        ...(user.dataViewOrganisationUnits?.map(ou => ou.id) || [])
                    ];
                    const userOrgUnitsInScope = userOrgUnitIds.filter(id => hierarchyOrgUnits.has(id));
                    const isInScope = userOrgUnitsInScope.length > 0;

                    if (isInScope) {
                        inScopeUsers.push(user);
                    }
                });

                // Filter to only in-scope users
                const matchedStaff = inScopeUsers.map(originalUser => {
                    const userTeis = originalUser ? allTeis.filter(tei => {
                        const usernameAttr = tei.attributes?.find(attr =>
                            attr.attribute === STAFF_ATTRIBUTES.USERNAME
                        );
                        return usernameAttr?.value === originalUser.userCredentials?.username;
                    }) : [];
                    
                    // ✅ Extract positionId from the TEI if available
                    let positionId = '';
                    if (userTeis.length > 0 && userTeis[0].attributes) {
                        const posAttr = userTeis[0].attributes.find(a => a.attribute === POSITION_ATTRIBUTES.POSITION_TITLE);
                        positionId = posAttr?.value || '';
                    }
                    
                    return {
                        ...originalUser,
                        hasTei: userTeis.length > 0,
                        tei: userTeis[0] || null,
                        allTeis: userTeis,
                        isInScope: true,
                        isTeiOnly: userTeis.length > 0 && !originalUser?.id,
                        positionId: positionId,
                    };
                });

                // Also include TEI-only records
                const teiOnlyRecords = allTeis
                    .filter(tei => {
                        const usernameAttr = tei.attributes?.find(attr =>
                            attr.attribute === STAFF_ATTRIBUTES.USERNAME
                        );
                        const username = usernameAttr?.value;
                        const matchedUser = allUsers.find(u => u.userCredentials?.username === username);
                        return !matchedUser && username;
                    })
                    .map(tei => {
                        const usernameAttr = tei.attributes?.find(attr =>
                            attr.attribute === STAFF_ATTRIBUTES.USERNAME
                        );
                        const firstNameAttr = tei.attributes?.find(attr =>
                            attr.attribute === STAFF_ATTRIBUTES.FIRST_NAME
                        );
                        const surnameAttr = tei.attributes?.find(attr =>
                            attr.attribute === STAFF_ATTRIBUTES.SURNAME
                        );
                        const emailAttr = tei.attributes?.find(attr =>
                            attr.attribute === STAFF_ATTRIBUTES.EMAIL
                        );
                        return {
                            id: tei.trackedEntityInstance,
                            trackedEntityInstance: tei.trackedEntityInstance,
                            tei: tei,
                            allTeis: [tei],
                            hasTei: true,
                            isTeiOnly: true,
                            userId: null,
                            username: usernameAttr?.value || 'Unknown',
                            firstName: firstNameAttr?.value || 'Unknown',
                            surname: surnameAttr?.value || 'Staff',
                            email: emailAttr?.value || '',
                            organisationUnits: [],
                            userCredentials: null,
                            lastLogin: null // TEI-only users have no login data
                        };
                    });

                const allStaff = [...matchedStaff, ...teiOnlyRecords];

                if (isMounted) {
                    setStaffMembers(allStaff);
                    
                    // ✅ Fetch activity data for all users with IDs
                    const userIds = matchedStaff
                        .filter(staff => staff.id)
                        .map(staff => staff.id)
                        .filter(id => id);
                    
                    if (userIds.length > 0) {
                        const activityData = await fetchUserActivity(userIds);
                        setUserActivityMap(activityData);
                    }
                    setLoadingActivity(false);
                }

            } catch (e) {
                console.error('❌ Fetch error:', e);
                if (isMounted) {
                    setError(e.message || 'Failed to load staff data');
                    setLoadingActivity(false);
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [engine, refreshTrigger]);

    const refreshStaffList = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    // Filter staff members based on search
    const filteredStaff = staffMembers.filter((staff) => {
        const fullName = `${staff.firstName || ''} ${staff.surname || ''}`.toLowerCase();
        const username = staff.userCredentials?.username?.toLowerCase() || staff.username?.toLowerCase() || '';
        const email = staff.email?.toLowerCase() || '';
        return (
            fullName.includes(search.toLowerCase()) ||
            username.includes(search.toLowerCase()) ||
            email.includes(search.toLowerCase())
        );
    });

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <CircularLoader small />
                <span style={{ marginLeft: '10px' }}>Loading staff data...</span>
            </div>
        );
    }

    if (error) return (
        <NoticeBox error title="Error">
            {error}
            <Button small onClick={() => window.location.reload()} style={{ marginTop: 8 }}>
                Retry
            </Button>
        </NoticeBox>
    );

    return (
        <div style={{ margin: '20px 0' }}>
            <h4>Staff Members ({filteredStaff.length})</h4>

            <InputField
                label="Search Staff"
                value={search}
                onChange={({ value }) => setSearch(value)}
                style={{ marginBottom: '2rem', width: '300px' }}
                placeholder="Search by name, username, or email..."
                helpText={`Searching ${staffMembers.length} staff members`}
            />

            {filteredStaff.length === 0 ? (
                <NoticeBox title="No staff found">
                    {search ? 'No staff members match your search' : 'No staff members found'}
                </NoticeBox>
            ) : (
                <div className="responsive-table" style={{ width: "100%", overflowX: "auto" }}>
                    {/* Header */}
                    <div className="table-row header" style={{
                        display: "flex",
                        fontWeight: "bold",
                        borderBottom: "2px solid #ccc",
                        padding: "12px 8px",
                        backgroundColor: "#f0f0f0",
                        marginTop: "20px",
                        minWidth: "900px"
                    }}>
                        <div style={{ flex: 1, padding: "0 8px" }}>Username</div>
                        <div style={{ flex: 2, padding: "0 8px" }}>Name</div>
                        <div style={{ flex: 2, padding: "0 8px" }}>Email</div>
                        <div style={{ flex: 2, padding: "0 8px" }}>Departments</div>
                        <div style={{ flex: 1.5, padding: "0 8px" }}>Last Login</div>
                        <div style={{ flex: 1.5, padding: "0 8px" }}>Status</div>
                        <div style={{ flex: 2, padding: "0 8px" }}>Actions</div>
                    </div>

                    {filteredStaff.map(staff => {
                        const isTeiOnly = staff.isTeiOnly || (staff.hasTei && !staff.id);
                        const teiId = staff.tei?.trackedEntityInstance ||
                            (staff.allTeis && staff.allTeis[0]?.trackedEntityInstance) ||
                            staff.trackedEntityInstance ||
                            staff.id;

                        const isProtected = isProtectedUser(staff);
                        
                        // ✅ Get activity data for this user
                        const activity = userActivityMap[staff.id] || { lastLogin: null, hasLoggedIn: false };
                        const lastLogin = activity.lastLogin || staff.lastLogin || null;
                        const status = getActivityStatus(lastLogin);
                        const lastLoginFormatted = formatLastLogin(lastLogin);
                        const lastLoginDuration = formatDuration(lastLogin);

                        return (
                            <div key={staff.id || teiId} className="table-row" style={{
                                display: "flex",
                                flexDirection: "row",
                                borderBottom: "1px solid #eee",
                                padding: "12px 8px",
                                alignItems: "center",
                                backgroundColor: isTeiOnly ? '#ffe6e6' : (isProtected ? '#e3f2fd' : (staff.hasTei ? 'white' : '#fff9e6')),
                                minWidth: "900px"
                            }}>
                                <div style={{ flex: 1, padding: "0 8px", wordBreak: "break-word" }}>
                                    {staff.userCredentials?.username || staff.username || '—'}
                                    {isProtected && (
                                        <span style={{
                                            display: 'inline-block',
                                            marginLeft: '8px',
                                            padding: '2px 8px',
                                            backgroundColor: '#1565c0',
                                            color: 'white',
                                            borderRadius: '12px',
                                            fontSize: '10px',
                                            fontWeight: 'bold'
                                        }}>
                                            PROTECTED
                                        </span>
                                    )}
                                    {isTeiOnly && (
                                        <span style={{
                                            display: 'inline-block',
                                            marginLeft: '8px',
                                            padding: '2px 8px',
                                            backgroundColor: '#ff6b6b',
                                            color: 'white',
                                            borderRadius: '12px',
                                            fontSize: '10px',
                                            fontWeight: 'bold'
                                        }}>
                                            NO USER
                                        </span>
                                    )}
                                </div>
                                <div style={{ flex: 2, padding: "0 8px", wordBreak: "break-word" }}>
                                    {staff.firstName} {staff.surname}
                                </div>
                                <div style={{ flex: 2, padding: "0 8px", wordBreak: "break-word" }}>
                                    {staff.email || '—'}
                                </div>
                                <div style={{ flex: 2, padding: "0 8px", wordBreak: "break-word", fontSize: '12px' }}>
                                    {staff.organisationUnits?.map(ou => `${ou.name}`).join(', ') || '—'}
                                </div>
                                <div style={{ flex: 1.5, padding: "0 8px", wordBreak: "break-word", fontSize: '12px' }}>
                                    {loadingActivity ? (
                                        <CircularLoader small />
                                    ) : (
                                        lastLoginFormatted
                                    )}
                                </div>
                                <div style={{ flex: 1.5, padding: "0 8px" }}>
                                    {loadingActivity ? (
                                        <CircularLoader small />
                                    ) : isTeiOnly ? (
                                        <span style={{ color: '#6c757d', fontSize: '12px' }}>N/A</span>
                                    ) : (
                                        <div style={{
                                            display: 'inline-block',
                                            padding: '4px 12px',
                                            borderRadius: '12px',
                                            backgroundColor: status.bg,
                                            color: status.color,
                                            fontWeight: '600',
                                            fontSize: '12px',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            <span style={{
                                                display: 'inline-block',
                                                width: '8px',
                                                height: '8px',
                                                borderRadius: '50%',
                                                backgroundColor: status.color,
                                                marginRight: '6px'
                                            }}></span>
                                            {status.label}
                                            {status.label !== 'Online' && status.label !== 'Today' && status.label !== 'Inactive' && (
                                                <span style={{ fontSize: '10px', opacity: 0.7, marginLeft: '4px' }}>
                                                    ({lastLoginDuration})
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div style={{ flex: 2, padding: "0 8px", display: "flex", gap: "8px" }}>
                                    {/* View Button */}
                                    <Button
                                        small
                                        icon={<IconView16 />}
                                        style={{
                                            backgroundColor: '#0c982a',
                                            border: '1px solid #ddd',
                                            borderRadius: '12px',
                                            padding: '4px 8px',
                                            fontSize: '14px',
                                            cursor: 'pointer',
                                            color: '#fff'
                                        }}
                                        onClick={() => {
                                            if (onView) {
                                                onView({
                                                    id: staff.id || staff.trackedEntityInstance,
                                                    userId: staff.id,
                                                    username: staff.userCredentials?.username || staff.username,
                                                    firstName: staff.firstName,
                                                    surname: staff.surname,
                                                    email: staff.email,
                                                    phoneNumber: staff.phoneNumber,
                                                    teiId: teiId,
                                                    hasTei: staff.hasTei,
                                                    isTeiOnly: isTeiOnly,
                                                    organisationUnits: staff.organisationUnits,
                                                    userRoles: staff.userCredentials?.userRoles,
                                                    attributes: staff.tei?.attributes || [],
                                                    positionId: staff.positionId || '',
                                                    lastLogin: lastLogin,
                                                    lastLoginDuration: lastLoginDuration,
                                                    status: status
                                                });
                                            }
                                        }}
                                        title="View staff details"
                                    >
                                        View
                                    </Button>

                                    {/* Edit Button */}
                                    <Button
                                        small
                                        primary
                                        icon={<IconEdit16 />}
                                        onClick={() => {
                                            if (isTeiOnly) {
                                                alert('Cannot edit: This staff member does not have a user account.');
                                                return;
                                            }
                                            if (staff.hasTei && staff.tei) {
                                                onEdit({
                                                    trackedEntityInstance: staff.tei?.trackedEntityInstance,
                                                    userId: staff.id,
                                                    username: staff.userCredentials?.username,
                                                    firstName: staff.firstName || '',
                                                    secondName: staff.surname || '',
                                                    email: staff.email || '',
                                                    phoneNumber: staff.phoneNumber || '',
                                                    userRole: staff.userCredentials?.userRoles?.[0]?.id || '',
                                                    department: '',
                                                    orgUnit: staff.organisationUnits?.[0]?.id || '',
                                                    orgUnitName: staff.organisationUnits?.[0]?.name || '',
                                                    assignedOu: staff.organisationUnits?.[0]?.id || '',
                                                    assignedOuName: staff.organisationUnits?.[0]?.name || '',
                                                    attributes: staff.tei?.attributes || [],
                                                    missingTEI: !staff.hasTei,
                                                    positionId: staff.positionId || '',
                                                    positionDetails: staff.positionDetails || null
                                                });
                                            } else {
                                                onEdit({
                                                    id: staff.id,
                                                    userId: staff.id,
                                                    username: staff.userCredentials?.username,
                                                    firstName: staff.firstName,
                                                    secondName: staff.surname,
                                                    email: staff.email,
                                                    orgUnit: staff.organisationUnits?.[0]?.id,
                                                    orgUnitName: staff.organisationUnits?.[0]?.name,
                                                    phoneNumber: '',
                                                    gender: '',
                                                    department: '',
                                                    userRole: '',
                                                    attributes: [],
                                                    missingTEI: true
                                                });
                                            }
                                        }}
                                        disabled={isTeiOnly}
                                        title={isTeiOnly ? "Cannot edit - no user account" : ""}
                                    >
                                        Edit
                                    </Button>

                                    {/* Delete Button */}
                                    <Button
                                        small
                                        destructive
                                        icon={<IconDelete16 />}
                                        onClick={() => {
                                            if (isProtected) {
                                                alert(`Cannot delete ${staff.userCredentials?.username || staff.username}: Protected system account.`);
                                                return;
                                            }
                                            if (!staff.hasTei && !teiId) {
                                                alert('No TEI record to delete.');
                                                return;
                                            }
                                            if (isTeiOnly) {
                                                if (!window.confirm(
                                                    `⚠️ This staff member (${staff.firstName} ${staff.surname}) does not have a user account.\n\nOnly the TEI record will be deleted. This action cannot be undone.\n\nAre you sure you want to continue?`
                                                )) return;
                                            }
                                            onDelete({
                                                teiId: teiId,
                                                userId: staff.id || null,
                                                username: staff.userCredentials?.username || staff.username || 'Unknown',
                                                firstName: staff.firstName || 'Unknown',
                                                surname: staff.surname || 'Staff',
                                                email: staff.email || '',
                                                hasTei: staff.hasTei,
                                                hasUser: !isTeiOnly && !!staff.id,
                                                isProtected: isProtected,
                                                isTeiOnly: isTeiOnly
                                            });
                                        }}
                                        disabled={(!staff.hasTei && !teiId) || isProtected || (deleteLoading && deleteLoading === (staff.id || staff.userId))}
                                        loading={deleteLoading && deleteLoading === (staff.id || staff.userId)}
                                        title={isProtected ? "Cannot delete - protected account" : (isTeiOnly ? "Delete TEI only" : "Delete staff")}
                                    >
                                        {isTeiOnly ? 'Delete TEI' : 'Delete'}
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default StaffList;